// Music director (FACE & FURY S7 SONIC). A thin, renderer-only observer that drives the two reactive
// music behaviors each frame off the sim SNAPSHOT + the renderer's own chain state. It NEVER touches
// Core, the Sim, determinism, or Commands — it only READS the snapshot the renderer already holds and
// calls the audio bus (which owns the WebAudio filter/gain plumbing). Deterministic-safe by
// construction: no writes back into the sim, no RNG, no timers.
//
//   (a) HELD-BREATH — while the human is mid-gauntlet (has routed an ARMED decoy this run AND a live
//       armed lockout still exists on the board) the music goes muffled + slightly ducked; the release
//       (threading the CHOMP → armedTraversals resets to 0, or the fence expiring) snaps it bright.
//       The OPEN is the payoff, so it is intentionally the satisfying half of the beat.
//   (b) COMBO / CHAIN LIFT — a hot combo (tier ≥3) or a live GAUNTLET CHAIN (≥2 consecutive threads)
//       swells the tension layer above the timer/hazard intensity mapping; reverts on the break.
import type { Snapshot } from '../../core';
import { audio } from '../audio';
import { PLAYER, AI } from './types';
import type { GameScene } from '../scenes/GameScene';

// FACE & FURY S9 #36 — the combo/chain LIFT is now MULTI-TIER (was a binary on/off that read flat at
// 71.6% ON). A hot combo (or a 2-thread chain) floors the tension layer; a ≥3 consecutive-thread chain
// SURGES it to full — two distinct heights so the escalation is audible, not a plateau.
/** Combo tier at/above which the tension layer gets the FLOOR lift. Matches the combo1 SFX milestone (c===3). */
const LIFT_COMBO_MIN = 3;
/** Consecutive-thread chain at/above which the FLOOR lift engages (mirrors the crescendo's chain-2 floor). */
const LIFT_CHAIN_MIN = 2;
/** Consecutive-thread chain at/above which the lift SURGES to full — the "you're on a run" crest. */
const LIFT_CHAIN_SURGE = 3;
/** The floor-tier lift amount (0..1). The surge tier is 1.0, so the two are clearly separated in height. */
const LIFT_FLOOR = 0.45;
/** VILLAIN presence ramp (BYT3's lead margin, human−rival perspective): silent until he's ahead. */
// S9 #35: deadzone 60→30 so the bassline surfaces on the smallest real lead (half a decoy penalty), not
// only in a blowout. INVARIANT (STRETCH broadcast): broadcast.menaceTakeMargin (80) must stay ≥ this
// deadzone so a #44 menace beat can never fire while the villain layer is silent — 80 ≥ 30 holds. Do NOT
// raise this above 80, and do NOT edit broadcast.ts.
const VILLAIN_LEAD_DEADZONE = 30;
const VILLAIN_LEAD_FULL = 300; // BYT3 ahead by ~3 correct beyond the deadzone → full villain swell

/** #35 last-seen rival score, to detect a rival-score increase frame-to-frame → a villain pulse. Module
 *  scope (one live match at a time); reset to -1 whenever the board isn't playing so a new board re-seeds. */
let prevRivalScore = -1;

/**
 * Per-frame director update. Bound onto GameScene as a field (S2 idiom) and called from tickFrame right
 * after updateMusicIntensity, so it reads the same snapshot the frame already rendered.
 */
export function updateMusicDirector(this: GameScene, snap: Snapshot): void {
  const human = snap.players.find((p) => p.id === PLAYER);
  // Board decided / no human / down → force-release everything so no muffle or lift bleeds into the
  // board-clear beat or the Result screen. (updateMusicIntensity/consumeEvents already ran this frame.)
  if (!human || snap.status !== 'playing' || !human.alive) {
    audio().setHeldBreath(false);
    audio().setMusicLift(0);
    audio().setVillainPresence(0); // no muffle/lift/villain bleeds into the board-clear or Result beat
    prevRivalScore = -1; // #35 re-seed so the next board's first rival score doesn't pulse from stale state
    return;
  }

  // (a) HELD-BREATH: the human is mid-route through the Gauntlet — they have traversed ≥1 armed decoy
  //     since their last eat AND a live armed lockout still exists on the board (so the fence is HOT
  //     right now, not a stale count). A stun suppresses it (no breath-hold while frozen). When the
  //     player threads the CHOMP, armedTraversals resets to 0 in Core → this goes false next frame →
  //     the filter opens (the payoff). A wandered-off arm expiring (≤0.6s) also releases cleanly.
  const stunned = human.stunnedUntilMs > snap.timeMs;
  const liveArm =
    human.armedTraversals >= 1 &&
    snap.cells.some((c) => c.armed && !c.eaten && c.armedUntilMs > snap.timeMs);
  audio().setHeldBreath(liveArm && !stunned);

  // (b) COMBO / CHAIN LIFT (#36 multi-tier): a ≥3 consecutive-thread chain SURGES the tension layer to
  //     full; a hot combo (or a 2-thread chain) floors it lower. Two distinct heights, so the run reads as
  //     an escalation instead of a flat plateau. chainStreak is the renderer's non-farmable thread count.
  const lift =
    this.chainStreak >= LIFT_CHAIN_SURGE
      ? 1
      : human.combo >= LIFT_COMBO_MIN || this.chainStreak >= LIFT_CHAIN_MIN
        ? LIFT_FLOOR
        : 0;
  audio().setMusicLift(lift);

  // (c) VILLAIN PRESENCE: BYT3's bassline swells in as he pulls clearly ahead — the sonic signature of the
  //     rival's dominance. Reuses the SAME snapshot read the HUD chip does (rival = players[AI]); 0 when the
  //     human leads or ties, ramping to 1 once BYT3 is ahead by more than the deadzone. No AI (practice/solo)
  //     → no rival player → presence stays 0 (the layer never surfaces).
  const rival = snap.players.find((p) => p.id === AI);
  const margin = rival ? rival.score - human.score : 0; // + = BYT3 ahead
  const presence = margin <= VILLAIN_LEAD_DEADZONE ? 0 : Math.min(1, (margin - VILLAIN_LEAD_DEADZONE) / VILLAIN_LEAD_FULL);
  audio().setVillainPresence(presence);

  // (d) #35 VILLAIN PULSE on a rival SCORE: BYT3 just banked points → a short additive bassline swell, so
  //     he's audible on every rival point (not only when clearly ahead). Edge-detected off his score; the
  //     first frame of a board (prevRivalScore < 0) only seeds the baseline (no pulse).
  if (rival) {
    if (prevRivalScore >= 0 && rival.score > prevRivalScore) audio().pulseVillain();
    prevRivalScore = rival.score;
  }
}
